import { OpeningDetailResponse } from 'api-clients/monolith/models/OpeningDetailResponse';
import { Loader2 } from 'lucide-react';
import { useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { ExternalLinkIcon } from '@/components/icons/external-link';
import { useRemoveOpeningFromPosting } from '@/components/pages/job-posting/use-remove-opening-from-posting';
import { Button } from '@/components/ui/button';
import { FormSelect } from '@/components/ui/select';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useAccountSlug } from '@/hooks/use-account-slug';
import { ZIPRECRUITER } from '@/types/channels';
import { domainMeta } from '@/utils/domain-meta';

import { JobDetailFormState } from '../types';
import { STATE_OPTIONS } from '../utils/constants';
import { DescriptionSection } from './description-section';
import { PostingTitle } from './posting-title';
import { useJobConstants } from './use-job-constants';
import { useUpdateOpeningDescription } from './use-update-opening-description';
interface ExpandedViewProps {
  opening: OpeningDetailResponse;
  payDisplay: string | null;
  toggleExpanded?: () => void;
  onRequestRefreshBanner?: () => void;
}

export function ExpandedView({
  opening,
  payDisplay,
  toggleExpanded: _toggleExpanded,
  onRequestRefreshBanner,
}: ExpandedViewProps) {
  const intl = useIntl();
  const accountSlug = useAccountSlug();
  const { getJobType, getJobHours } = useJobConstants();
  const { hookForm, selectedOpeningIds, selectedChannelSlugs } =
    useJobPostingContext();
  const isZipRecruiterSelected = selectedChannelSlugs.includes(ZIPRECRUITER);
  const removeOpeningFromPosting = useRemoveOpeningFromPosting(
    opening.external_id,
  );

  const {
    setValue,
    formState: { errors },
    getValues,
    control,
    trigger,
    watch,
  } = hookForm;

  /**
   * The server-managed fields (industry, jobHours, jobType, experience,
   * locationAddress) are only required — and therefore only get the `*` asterisk —
   * when this opening appears in at least one Vonq channel's `openingBudgets`.
   * This mirrors the gate in `useJobDetailsSchema`'s superRefine.
   */
  const channels = watch('channels');
  const isInVonqChannel = useMemo(
    () =>
      Object.values(channels ?? {}).some(
        channel =>
          !!channel?.vonqDetails &&
          opening.external_id in (channel.openingBudgets ?? {}),
      ),
    [channels, opening.external_id],
  );

  const jobDetailErrors = errors?.jobDetails?.[opening.external_id];
  const hasError = !!jobDetailErrors;
  const stateCodeErrorMessage = jobDetailErrors?.stateCode?.message;

  const locationFieldLabels = {
    locationAddress: intl.formatMessage({
      defaultMessage: 'Location',
      description: 'Location field label used in validation error message',
    }),
    postalCode: intl.formatMessage({
      defaultMessage: 'Postal code',
      description: 'Postal code field label used in validation error message',
    }),
    city: intl.formatMessage({
      defaultMessage: 'City',
      description: 'City field label used in validation error message',
    }),
  } as const;

  const missingLocationFieldLabels = (
    Object.keys(locationFieldLabels) as Array<keyof typeof locationFieldLabels>
  )
    .filter(key => jobDetailErrors?.[key]?.message)
    .map(key => locationFieldLabels[key]);

  const requiredFieldLabels = {
    industry: intl.formatMessage({
      defaultMessage: 'Industry',
      description: 'Industry of the job',
    }),
    jobHours: intl.formatMessage({
      defaultMessage: 'Job hours',
      description: 'Job hours of the job',
    }),
    jobType: intl.formatMessage({
      defaultMessage: 'Job type',
      description: 'Job type of the job',
    }),
    experience: intl.formatMessage({
      defaultMessage: 'Years of experience',
      description: 'Years of experience of the job',
    }),
  } as const;

  const payLabel = intl.formatMessage({
    defaultMessage: 'Pay',
    description: 'Pay of the job',
  });

  const missingRequiredFieldLabels = (
    Object.keys(requiredFieldLabels) as Array<keyof typeof requiredFieldLabels>
  )
    .filter(key => jobDetailErrors?.[key]?.message)
    .map(key => requiredFieldLabels[key]);

  const jobDetailsErrorMessage =
    missingRequiredFieldLabels.length > 0
      ? intl.formatMessage(
          {
            defaultMessage:
              '{count, plural, one {{fields} is required} other {{fields} are required}}',
            description:
              'Combined error shown beneath the job details summary when one or more required job fields are missing. {fields} is a localized conjunction list of field names like "Industry, Job hours and Job type"; {count} drives singular/plural agreement of the verb.',
          },
          {
            count: missingRequiredFieldLabels.length,
            fields: intl.formatList(missingRequiredFieldLabels, {
              type: 'conjunction',
            }),
          },
        )
      : undefined;

  const jobSettingsUrl = `${domainMeta.monolithOrigin}/${accountSlug}/openings/${opening.slug}`;
  const locationSettingsUrl = `${domainMeta.monolithOrigin}/${accountSlug}/locations#/${opening.location_external_id}/edit`;

  /** Capture snapshot of form state when component mounts to allow for canceling the edit. */
  const descriptionSnapshotRef = useRef<JobDetailFormState>(
    JSON.parse(
      JSON.stringify(getValues(`jobDetails.${opening.external_id}`)),
    ) as JobDetailFormState,
  );

  const collapseOpening = () => {
    setValue(`jobDetails.${opening.external_id}.isExpanded`, false);
  };

  const { updateOpeningDescription, isUpdatingOpening } =
    useUpdateOpeningDescription({
      onSuccess: () => {
        collapseOpening();

        // Update the snapshot with the current values
        descriptionSnapshotRef.current = JSON.parse(
          JSON.stringify(getValues(`jobDetails.${opening.external_id}`)),
        ) as JobDetailFormState;
      },
    });

  const handleSaveClick = async () => {
    const isValid = await trigger(`jobDetails.${opening.external_id}`);
    if (!isValid) return;

    updateOpeningDescription({
      openingExternalId: opening.external_id,
      requestBody: {
        description: getValues(`jobDetails.${opening.external_id}.description`),
        state_code: getValues(`jobDetails.${opening.external_id}.stateCode`),
      },
    });
  };

  const handleCancelClick = () => {
    setValue(
      `jobDetails.${opening.external_id}`,
      descriptionSnapshotRef.current,
    );
    collapseOpening();
  };

  return (
    <div className="border-fx-neutral-200 flex flex-col gap-6 rounded-lg border bg-white p-6 shadow-sm">
      {/* Posting title */}
      <PostingTitle openingId={opening.external_id} />

      <div className="bg-fx-neutral-200 h-px w-full" />

      {/* Job type, shift, pay range, industry, job hours */}
      <div className="flex flex-col gap-2">
        <div className="grid auto-cols-auto grid-flow-col items-center gap-6">
          <PropertyItem
            title={requiredFieldLabels.industry}
            value={opening.job_industry}
            isRequired={isInVonqChannel}
          />

          <PropertyItem
            title={requiredFieldLabels.jobHours}
            value={getJobHours(opening.job_hours)}
            isRequired={isInVonqChannel}
          />

          <PropertyItem
            title={requiredFieldLabels.jobType}
            value={getJobType(opening.job_type)}
            isRequired={isInVonqChannel}
          />

          <PropertyItem
            title={requiredFieldLabels.experience}
            value={opening.years_of_experience}
            isRequired={isInVonqChannel}
          />

          <PropertyItem title={payLabel} value={payDisplay} />

          <div className="justify-self-end">
            <EditButton
              toUrl={jobSettingsUrl}
              onClick={() => onRequestRefreshBanner?.()}
            />
          </div>
        </div>
        {jobDetailsErrorMessage && (
          <Typography variant="small1" className="!text-fx-rose">
            {jobDetailsErrorMessage}
          </Typography>
        )}
      </div>

      <div className="bg-fx-neutral-200 h-px w-full" />

      {/* Location, Brand */}
      <div className="flex flex-col gap-2">
        <div className="grid grid-cols-[auto_1fr] items-center gap-6">
          <PropertyItem
            title={intl.formatMessage({
              defaultMessage: 'Location',
              description: 'Location of the job',
            })}
            value={
              opening.remote
                ? intl.formatMessage({
                    defaultMessage: 'Remote',
                    description: 'Remote location of the job',
                  })
                : opening.location_address
            }
            isRequired={isInVonqChannel}
          />

          <div className="justify-self-end">
            <EditButton
              toUrl={locationSettingsUrl}
              onClick={() => onRequestRefreshBanner?.()}
            />
          </div>
        </div>
        {missingLocationFieldLabels.length > 0 && (
          <Typography variant="small1" className="!text-fx-rose">
            {intl.formatMessage(
              {
                defaultMessage:
                  '{count, plural, one {{fields} is required} other {{fields} are required}}',
                description:
                  'Combined error shown beneath the location summary when one or more required location fields are missing. {fields} is a localized conjunction list of field names like "Postal code and City"; {count} drives singular/plural agreement of the verb.',
              },
              {
                count: missingLocationFieldLabels.length,
                fields: intl.formatList(missingLocationFieldLabels, {
                  type: 'conjunction',
                }),
              },
            )}
          </Typography>
        )}
      </div>

      {opening.remote && isZipRecruiterSelected && (
        <FormSelect
          name={`jobDetails.${opening.external_id}.stateCode`}
          control={control}
          options={[...STATE_OPTIONS]}
          isMultiSelect={false}
          placeholder={intl.formatMessage({
            defaultMessage: 'Select state',
            description: 'Placeholder for state select',
          })}
          label={intl.formatMessage({
            defaultMessage: 'Hiring State',
            description: 'Label for state select',
          })}
          description={intl.formatMessage({
            defaultMessage:
              'Note: Used for job board distribution and targeting. This does not restrict applicant eligibility.',
            description: 'Description for state select',
          })}
          required
          error={stateCodeErrorMessage}
        />
      )}

      <PropertyItem
        title={intl.formatMessage({
          defaultMessage: 'Brand',
          description: 'Brand name of the job',
        })}
        value={opening.brand_name}
      />

      <div className="bg-fx-neutral-200 h-px w-full" />

      <DescriptionSection openingId={opening.external_id} />

      {selectedOpeningIds.length > 1 && (
        <div className="flex justify-between gap-6">
          <Button
            type="button"
            variant="ghost"
            className="!text-fx-neutral-600"
            onClick={removeOpeningFromPosting}
          >
            <FormattedMessage
              defaultMessage="Remove from posting"
              description="Remove the channel from the job posting"
            />
          </Button>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancelClick}
            >
              <FormattedMessage
                defaultMessage="Cancel"
                description="Cancel the channel changes"
              />
            </Button>
            <Button
              type="button"
              onClick={() => void handleSaveClick()}
              disabled={hasError}
            >
              {isUpdatingOpening && <Loader2 className="animate-spin" />}
              <FormattedMessage
                defaultMessage="Save"
                description="Save the channel changes"
              />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function PropertyItem({
  title,
  value,
  isRequired = false,
}: {
  title: string;
  value: string | number | null;
  isRequired?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1 overflow-hidden">
      <Typography variant="caption" className="!text-fx-neutral-500">
        {title} {isRequired && <span className="text-fx-rose">*</span>}
      </Typography>
      <Typography variant="body1" className="truncate">
        {value ? value.toString() : '-'}
      </Typography>
    </div>
  );
}

function EditButton({
  toUrl,
  onClick,
}: {
  toUrl: string;
  onClick?: () => void;
}) {
  return (
    <Button variant="secondary" asChild>
      <Link
        to={toUrl}
        target="_blank"
        onClick={() => {
          onClick?.();
        }}
      >
        <ExternalLinkIcon className="text-fx-primary" />
        <FormattedMessage defaultMessage="Edit" description="Edit button" />
      </Link>
    </Button>
  );
}
